import React from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, AlertTriangle, AlertCircle, Loader2, RefreshCw, X, Scale } from 'lucide-react';
import { cn } from '../../utils/cn';
import { CHIP_PILL } from '../../utils/chipColors';
import Tooltip from '../ui/Tooltip';
import Modal from '../ui/Modal';
import ReconciliationView from './ReconciliationView';
import { useReconciliationStatus } from '../../hooks/useReconciliationStatus';

type State = 'loading' | 'healthy' | 'mismatch' | 'error' | 'idle';

interface ChipStyle {
  classes: string;
  Icon: React.ComponentType<{ className?: string }>;
  label: string;
}

/**
 * Compact health pulse — placed in pages users visit often (e.g. Schedule)
 * to surface a single-glance answer to "is the platform reconciled with my
 * brokers right now?". Click → open the full per-account drill-down inside
 * a modal (no navigation away from the current page). The hover tooltip
 * carries the per-account breakdown so users rarely need to drill in.
 */
const ReconciliationStatusPill: React.FC = () => {
  const { data, loading, error, forceRefresh } = useReconciliationStatus();
  const [open, setOpen] = React.useState(false);

  const state: State = (() => {
    if (loading && !data) return 'loading';
    if (error) return 'error';
    if (!data) return 'idle';
    const a = data.aggregate;
    if (a.totalAccounts === 0) return 'idle';
    if (a.errorAccountCount > 0) return 'error';
    if (a.allHealthy) return 'healthy';
    return 'mismatch';
  })();

  const style = chipStyle(state, data);
  const tooltip = buildTooltip(state, data, error);

  const handleClick = () => setOpen(true);
  const handleRefresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    forceRefresh();
  };
  const handleClose = () => {
    setOpen(false);
    // Refresh the pulse status when the user closes the modal — they may
    // have just exited / synced something while inside the dialog.
    forceRefresh();
  };

  // The pill lives inside <PageHeader>, whose outer wrapper applies
  // `backdrop-blur-md`. On WebKit (Safari, iOS) `backdrop-filter` creates a
  // containing block for `position: fixed` descendants, which would anchor
  // the modal to the PageHeader box rather than the viewport. Portal the
  // modal to document.body so its `fixed inset-0` resolves against the
  // viewport regardless of where the pill is rendered.
  //
  // `h-[85vh]` fixes the dialog height so the body always reserves a
  // predictable scroll area regardless of how many instruments are loaded
  // (Modal's default is `max-h-[90vh]`, which lets the dialog shrink to
  // content and the inner table never gets a scrollbar). The header is
  // `shrink-0` so it stays pinned; only the body scrolls.
  const modal = (
    <Modal
      isOpen={open}
      onClose={handleClose}
      maxWidth="6xl"
      ariaLabel="Broker vs Platform reconciliation"
      className="h-[85vh]"
    >
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
        <div className="flex items-center gap-2">
          <Scale className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Reconciliation</h2>
        </div>
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          className="inline-flex items-center justify-center w-8 h-8 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto min-h-0 px-3 sm:px-6 py-3 sm:py-4">
        <ReconciliationView />
      </div>
    </Modal>
  );

  return (
    <>
      <Tooltip text={tooltip}>
        <button
          type="button"
          onClick={handleClick}
          aria-label={`Reconciliation status: ${style.label}. Click to view details.`}
          className={cn(
            // Outline comes from CHIP_PILL.X (ring-1 ring-{color}-200) inside
            // style.classes — do NOT add a `border` class here or Tailwind's
            // default currentColor border will stack with the ring, producing
            // two visible outlines.
            'inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1.5 rounded-md text-xs font-medium transition-colors',
            style.classes,
          )}
        >
          <style.Icon className={cn('w-3.5 h-3.5', state === 'loading' && 'animate-spin')} />
          <span>{style.label}</span>
          <span
            role="button"
            tabIndex={-1}
            onClick={handleRefresh}
            className={cn(
              'inline-flex items-center justify-center w-5 h-5 rounded',
              'hover:bg-black/5 dark:hover:bg-white/10 transition-colors',
              loading && 'opacity-60 pointer-events-none',
            )}
            aria-label="Refresh reconciliation status"
          >
            <RefreshCw className={cn('w-3 h-3', loading && 'animate-spin')} />
          </span>
        </button>
      </Tooltip>
      {open && createPortal(modal, document.body)}
    </>
  );
};

// Page-level reconciliation pill — bg + text + outline sourced from CHIP_PILL
// canonical palette; hover state added inline per status. NOTE: CHIP_PILL uses
// `ring-1 ring-{color}-200` for its outline (Family B convention); this site's
// previous styling used `border border-{color}-200` — visually similar but
// technically different. Adopting CHIP_PILL for canonical alignment.
function chipStyle(state: State, data: ReturnType<typeof useReconciliationStatus>['data']): ChipStyle {
  switch (state) {
    case 'healthy':
      return {
        Icon: CheckCircle2,
        label: 'Reconciled',
        classes: `${CHIP_PILL.HEALTHY} hover:bg-green-100 dark:hover:bg-green-900/30`,
      };
    case 'mismatch': {
      const ct = data?.aggregate.totalMismatches ?? 0;
      return {
        Icon: AlertTriangle,
        label: ct === 1 ? '1 mismatch' : `${ct} mismatches`,
        classes: `${CHIP_PILL.ERROR} hover:bg-red-100 dark:hover:bg-red-900/30`,
      };
    }
    case 'error':
      return {
        Icon: AlertCircle,
        label: 'Check failed',
        classes: `${CHIP_PILL.ATTENTION} hover:bg-amber-100 dark:hover:bg-amber-900/30`,
      };
    case 'loading':
      return {
        Icon: Loader2,
        label: 'Checking…',
        classes: CHIP_PILL.NEUTRAL,
      };
    case 'idle':
    default:
      return {
        Icon: AlertCircle,
        label: 'No accounts',
        classes: CHIP_PILL.NEUTRAL,
      };
  }
}

function buildTooltip(state: State, data: ReturnType<typeof useReconciliationStatus>['data'], error: string | null): string {
  if (state === 'loading') return 'Checking broker positions against platform...';
  if (state === 'error' && error && !data) return error;
  if (!data) return 'Reconciliation status unavailable';
  const a = data.aggregate;
  if (a.totalAccounts === 0) return 'No enabled broker accounts to reconcile';

  const lines: string[] = [];
  if (state === 'healthy') {
    lines.push(`All ${a.totalAccounts} ${pluralize(a.totalAccounts, 'account')} reconciled`);
  } else if (state === 'mismatch') {
    lines.push(`${a.mismatchedAccountCount} of ${a.totalAccounts} ${pluralize(a.totalAccounts, 'account')} have mismatches`);
    lines.push(`${a.totalMismatches} ${pluralize(a.totalMismatches, 'instrument')} differ`);
    if (a.totalSideMismatches > 0) {
      lines.push(`${a.totalSideMismatches} side mismatch${a.totalSideMismatches === 1 ? '' : 'es'}`);
    }
  } else if (state === 'error') {
    lines.push(`${a.errorAccountCount} of ${a.totalAccounts} ${pluralize(a.totalAccounts, 'account')} could not be checked`);
  }
  // Per-account preview — short list, then click-through for details.
  const preview = data.accounts.slice(0, 3).map(acc => {
    const tag = acc.status === 'matched' ? 'OK' : acc.status === 'mismatched' ? 'MISMATCH' : 'ERROR';
    return `• ${acc.broker} ${acc.clientId} — ${tag}`;
  });
  if (preview.length) lines.push('', ...preview);
  if (data.accounts.length > 3) lines.push(`+${data.accounts.length - 3} more`);
  lines.push('', 'Click to view details');
  return lines.join('\n');
}

function pluralize(n: number, word: string): string {
  return n === 1 ? word : `${word}s`;
}

export default ReconciliationStatusPill;
